import { Trophy, Sparkles, Check } from "lucide-react";
import { fmtSecs } from "./utils";
import { T } from "../../lib/i18n";

export function ResultsCard({ attempt, leader, onBack }) {
  const rank = leader.find((r) => r.guest_id === attempt.guest_id)?.rank || null;
  const pct = attempt.completion_percentage ?? 0;
  return (
    <div className="card-elev p-5 space-y-3" data-testid="crossword-results">
      <div className="flex items-center gap-2 text-primary-motif">
        <Sparkles className="w-4 h-4" />
        <span className="font-display text-xl text-motif">{T("Crossword Submitted")}</span>
      </div>
      <p className="text-sm text-motif-muted">{T("Nice work — every correct answer counted toward your score.")}</p>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Stat label={T("Correct Answers")} value={`${attempt.correct_clue_count ?? 0} ${T("of")} ${attempt.total_clue_count ?? 0}`} testid="crossword-result-correct" />
        <Stat label={T("Puzzle Completion")} value={`${pct}%`} testid="crossword-result-pct" />
        <Stat label={T("Final Score")} value={`${(attempt.final_score ?? 0).toLocaleString()} ${T("points")}`} testid="crossword-result-score" />
        <Stat label={T("Time Used")} value={fmtSecs(attempt.elapsed_seconds)} testid="crossword-result-time" />
        <Stat label={T("Hints Used")} value={String(attempt.hint_count ?? 0)} testid="crossword-result-hints" />
        <Stat label={T("Leaderboard")} value={rank ? `#${rank}` : "—"} testid="crossword-result-rank" />
      </div>
      <div className="grid grid-cols-2 max-[319px]:grid-cols-1 gap-2 pt-1">
        <button
          onClick={() => document.querySelector('[data-testid="crossword-leaderboard"]')?.scrollIntoView({ behavior: "smooth" })}
          className="btn-ghost text-sm h-11 flex items-center justify-center"
          data-testid="crossword-view-leaderboard"
        >
          {T("View Leaderboard")}
        </button>
        {onBack && (
          <button onClick={onBack} className="btn-primary text-sm h-11 flex items-center justify-center" data-testid="crossword-return-games">
            {T("Return to Games")}
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl p-3 bg-accent-motif flex flex-col min-h-[84px]" data-testid={testid}>
      <div className="text-[10px] tracking-luxe text-motif-muted text-center">{label.toUpperCase()}</div>
      <div className="flex-1 flex items-center justify-center">
        <div className="font-display text-lg text-motif text-center leading-tight">{value}</div>
      </div>
    </div>
  );
}

export function ClueList({ clues, dirName, activeEntry, entryFilled, onPick, testid }) {
  const isActiveList = activeEntry && activeEntry.dir === dirName;
  if (!clues || clues.length === 0) return null;
  return (
    <ol className="space-y-1" data-testid={testid}>
      {clues.map((c) => {
        const active = isActiveList && activeEntry.number === c.number;
        const done = entryFilled(c);
        return (
          <li key={`${dirName}-${c.number}`}>
            <button
              type="button"
              onClick={() => onPick(c)}
              className="w-full text-left flex gap-2 items-start text-sm rounded-lg px-2 py-1.5 transition"
              style={{ background: active ? "var(--motif-accent)" : "transparent", opacity: done && !active ? 0.65 : 1 }}
              data-testid={`${testid}-${c.number}`}
            >
              <span className="font-display w-6 flex-shrink-0 text-primary-motif">{c.number}.</span>
              <span className="text-motif leading-snug flex-1">{c.text}</span>
              {done && <Check className="w-3.5 h-3.5 text-primary-motif flex-shrink-0 mt-0.5" aria-label="Completed" />}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export function Leaderboard({ rows }) {
  return (
    <div className="card-elev p-3" data-testid="crossword-leaderboard">
      <div className="flex items-center gap-2 text-[10px] tracking-luxe text-primary-motif mb-2">
        <Trophy className="w-3.5 h-3.5" /> {T("LEADERBOARD")}
      </div>
      {rows.length === 0 && <div className="text-motif-muted text-sm italic">{T("No finishers yet — be the first.")}</div>}
      <ol className="space-y-1.5">
        {rows.map((r) => (
          <li
            key={r.guest_id || r.guest_name}
            className={`flex items-center gap-2 text-sm ${r.rank <= 3 ? "text-primary-motif font-display" : "text-motif"}`}
            data-testid={`crossword-leader-${r.rank}`}
          >
            <span className="w-5 text-right flex-shrink-0">{r.rank}.</span>
            <span className="flex-1 min-w-0 truncate">
              {r.guest_name}
              {r.table_name && <span className="text-[10px] text-motif-muted ml-1">· {r.table_name}</span>}
            </span>
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0"
              style={{
                background: r.submission_type === "COMPLETE" ? "var(--motif-primary)" : "var(--motif-accent)",
                color: r.submission_type === "COMPLETE" ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
              }}
              data-testid={`crossword-leader-${r.rank}-badge`}
            >
              {r.submission_type === "COMPLETE" ? T("Complete") : T("Partial")}
            </span>
            <span className="text-[10px] text-motif-muted flex-shrink-0">{r.correct_clue_count}/{r.total_clue_count}</span>
            <span className="tabular-nums font-medium flex-shrink-0">{(r.final_score ?? 0).toLocaleString()}</span>
            <span className="tabular-nums text-[10px] text-motif-muted flex-shrink-0">{fmtSecs(r.elapsed_seconds)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
